import { create } from 'zustand';

const STORAGE_KEY = 'cosvision.sidebar.expanded';

function readInitial(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    // localStorage unavailable (Safari private mode etc.) — fall through.
  }
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(min-width: 1280px)').matches;
  }
  return true;
}

interface SidebarState {
  /** True = full-width rail (text labels). False = 64px icon rail. */
  isExpanded: boolean;
  setExpanded: (next: boolean) => void;
  toggle: () => void;
  /**
   * Mobile off-canvas drawer visibility (below the `md` breakpoint). Kept
   * separate from `isExpanded` (which only governs the desktop rail's
   * collapsed width) and intentionally NOT persisted — a fresh page load
   * should always start with the drawer closed.
   */
  isMobileOpen: boolean;
  openMobile: () => void;
  closeMobile: () => void;
  toggleMobile: () => void;
}

/**
 * Sidebar collapsed/expanded state. Default seeded from
 * `(min-width: 1280px)` so a desktop CO gets the full rail and a tablet
 * CO gets icons — then the choice is persisted to localStorage so the
 * preference survives reloads.
 */
export const useSidebarStore = create<SidebarState>((set) => ({
  isExpanded: readInitial(),
  setExpanded: (next) => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // localStorage unavailable — state-only fallback is fine.
      }
    }
    set({ isExpanded: next });
  },
  toggle: () =>
    set((s) => {
      const next = !s.isExpanded;
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(STORAGE_KEY, String(next));
        } catch {
          // ignore
        }
      }
      return { isExpanded: next };
    }),
  isMobileOpen: false,
  openMobile: () => set({ isMobileOpen: true }),
  closeMobile: () => set({ isMobileOpen: false }),
  toggleMobile: () => set((s) => ({ isMobileOpen: !s.isMobileOpen })),
}));
